import React, { useState, useEffect } from 'react';
import {
    Tag,
    Shirt,
    Shield,
    Hammer,
    Package,
    HardHat,
    Zap,
    Wind,
    Thermometer,
    Droplets,
    Construction,
    AlertTriangle,
    Plus,
    ChevronLeft,
    Edit,
    Trash2,
    Search,
    Filter,
    Download,
    CheckCircle2,
    XCircle,
    Layers
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
    Dialog,
    DialogContent
} from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';
import { cn } from '../../components/ui/utils';
import AddCategoryModal from './AddCategoryModal';

interface ItemCategoriesProps {
    onBack: () => void;
}

const ICON_MAP: Record<string, any> = {
    Shirt, Shield, HardHat, Hammer, Package, Zap, Wind, Thermometer, Droplets, Construction, AlertTriangle, Tag
};

const COLOR_MAP: Record<string, { bg: string, text: string, border: string }> = {
    blue: { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-100' },
    orange: { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-100' },
    green: { bg: 'bg-green-50', text: 'text-green-600', border: 'border-green-100' },
    purple: { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-100' },
    red: { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-100' },
    cyan: { bg: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-100' },
    yellow: { bg: 'bg-yellow-50', text: 'text-yellow-600', border: 'border-yellow-100' },
    pink: { bg: 'bg-pink-50', text: 'text-pink-600', border: 'border-pink-100' },
    slate: { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-100' },
};

const ItemCategories: React.FC<ItemCategoriesProps> = ({ onBack }) => {
    const [categories, setCategories] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState<any>(null);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const data = await api.get('/api/inventory/categories');
            setCategories(data);
        } catch (err) {
            console.error(err);
            toast.error('Failed to fetch categories');
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (category: any) => {
        setSelectedCategory(category);
        setIsAddModalOpen(true);
    };

    const handleDelete = async () => {
        if (!selectedCategory) return;
        try {
            await api.delete(`/api/inventory/categories/${selectedCategory.id}`);
            toast.success('Category deleted successfully');
            fetchData();
            setIsDeleteModalOpen(false);
        } catch (err: any) {
            toast.error(err.message || 'Failed to delete category');
        }
    };

    const filteredCategories = categories.filter(c =>
        c.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3 sm:gap-4">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={onBack}
                        className="rounded-xl flex items-center gap-1 sm:gap-2 text-gray-400 hover:text-gray-900 -ml-2"
                    >
                        <ChevronLeft size={18} />
                        <span className="hidden sm:inline">Settings</span>
                    </Button>
                    <div>
                        <h1 className="text-2xl font-bold">Item Categories</h1>
                        <p className="hidden sm:block text-sm text-gray-500 font-medium">Classify and organize your inventory items</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <Button variant="outline" className="flex-1 sm:flex-initial rounded-xl h-10 gap-2 border-gray-100 font-bold shadow-sm hover:bg-gray-50 transition-all text-xs">
                        <Download size={16} />
                        Export
                    </Button>
                    <Button
                        onClick={() => { setSelectedCategory(null); setIsAddModalOpen(true); }}
                        className="flex-1 sm:flex-initial rounded-xl h-10 bg-slate-900 hover:bg-slate-800 text-white gap-2 font-bold shadow-lg shadow-slate-900/10 transition-all text-xs"
                    >
                        <Plus size={16} />
                        Add Category
                    </Button>
                </div>
            </div>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-6">
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-blue-50 flex items-center justify-center text-blue-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <Tag size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider">Total Categories</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{categories.length}</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <CheckCircle2 size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider">Active Categories</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">{categories.filter(c => c.isActive).length}</p>
                    </div>
                </div>
                <div className="bg-white p-4 sm:p-6 rounded-[24px] border border-gray-100 shadow-sm flex items-center gap-4 group hover:shadow-md transition-all duration-300">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-2xl bg-purple-50 flex items-center justify-center text-purple-500 transition-transform group-hover:scale-110 duration-300 shrink-0">
                        <Package size={18} className="sm:w-5 sm:h-5" />
                    </div>
                    <div>
                        <p className="text-[10px] sm:text-xs font-bold text-gray-400 uppercase tracking-wider">Total Items</p>
                        <p className="text-lg sm:text-xl font-black text-gray-900 mt-0.5">--</p>
                    </div>
                </div>
            </div>

            {/* Filter Bar */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 bg-white p-3 sm:p-4 rounded-[24px] sm:rounded-[28px] border border-gray-100 shadow-sm">
                <div className="w-full sm:flex-1 relative group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-blue-500 transition-colors" size={18} />
                    <Input
                        placeholder="Search categories..."
                        className="pl-12 rounded-2xl border-gray-100 bg-gray-50/50 h-11 transition-all focus:bg-white text-sm"
                        value={searchQuery}
                        onChange={e => setSearchQuery(e.target.value)}
                    />
                </div>
                <Button variant="outline" className="w-full sm:w-auto rounded-2xl h-11 gap-2 border-gray-100 font-bold px-6 hover:bg-gray-50 text-xs text-gray-500">
                    <Filter size={16} className="text-gray-400" />
                    All Statuses
                </Button>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {filteredCategories.map((c) => {
                    const IconComp = ICON_MAP[c.icon] || Tag;
                    const colorStyles = COLOR_MAP[c.color] || COLOR_MAP.blue;

                    return (
                        <div key={c.id} className="bg-white rounded-[32px] border border-gray-100 shadow-sm overflow-hidden group hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col h-full">
                            <div className="p-6 sm:p-8 space-y-6 flex-1">
                                {/* Card Header */}
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3 sm:gap-4">
                                        <div className={cn(
                                            "w-10 h-10 sm:w-12 sm:h-12 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm",
                                            colorStyles.bg,
                                            colorStyles.text,
                                        )}>
                                            <IconComp size={20} className="sm:w-6 sm:h-6" />
                                        </div>
                                        <div className="max-w-[150px] sm:max-w-none">
                                            <h3 className="text-base sm:text-lg font-bold text-gray-900 leading-tight truncate" title={c.name}>{c.name}</h3>
                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                <div className={cn("w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full", c.isActive ? "bg-green-500" : "bg-gray-300")} />
                                                <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-wider">{c.isActive ? 'Active Category' : 'Disabled'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <Badge className={cn("rounded-lg px-2 sm:px-2.5 py-0.5 sm:py-1 text-[8px] sm:text-[10px] font-bold uppercase tracking-wider", c.isActive ? "bg-green-50 text-green-600 border-green-100" : "bg-gray-50 text-gray-400 border-gray-100")}>
                                        {c.isActive ? 'Active' : 'Inactive'}
                                    </Badge>
                                </div>

                                {/* Category Summary */}
                                <div className="bg-gray-50/50 rounded-2xl p-3 sm:p-4 flex items-center gap-3 sm:gap-4 border border-gray-100/50 hover:bg-gray-50 transition-colors">
                                    <div className={cn("w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white flex items-center justify-center shadow-sm border border-gray-100 shrink-0", colorStyles.text)}>
                                        <Layers size={16} className="sm:w-4.5 sm:h-4.5" />
                                    </div>
                                    <div>
                                        <p className="text-xs sm:text-sm font-bold text-gray-800 leading-none mb-1">
                                            Sub-items Tracking
                                        </p>
                                        <div className="flex items-center gap-1">
                                            <p className="text-[8px] sm:text-[10px] text-gray-400 font-bold uppercase tracking-wider">Organized Hierarchy</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Additional Details */}
                                <div className="space-y-3 px-1">
                                    <div className="flex items-center gap-3 group/info">
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gray-50 flex items-center justify-center text-gray-400 group-hover/info:bg-blue-50 group-hover/info:text-blue-500 transition-colors shrink-0">
                                            <Package size={12} className="sm:w-3.5 sm:h-3.5" />
                                        </div>
                                        <span className="text-[10px] sm:text-xs font-semibold text-gray-600">0 Items linked</span>
                                    </div>
                                    <p className="text-[9px] sm:text-[11px] text-gray-400 leading-relaxed italic">
                                        Define rules and limits for all items grouped within this category.
                                    </p>
                                </div>
                            </div>

                            {/* Actions */}
                            <div className="p-6 sm:p-8 pt-0 flex gap-2">
                                <Button
                                    variant="outline"
                                    className="flex-1 rounded-2xl h-10 sm:h-11 text-[10px] sm:text-xs font-bold gap-2 border-gray-100 shadow-sm hover:border-blue-100 hover:text-blue-600 transition-all hover:bg-blue-50/50"
                                    onClick={() => handleEdit(c)}
                                >
                                    <Edit size={14} />
                                    Edit
                                </Button>
                                <Button
                                    variant="outline"
                                    className="w-10 sm:w-11 rounded-2xl h-10 sm:h-11 text-xs font-bold border-gray-100 shadow-sm hover:border-red-100 hover:text-red-600 transition-all hover:bg-red-50/50 group/del"
                                    onClick={() => { setSelectedCategory(c); setIsDeleteModalOpen(true); }}
                                >
                                    <Trash2 size={14} className="group-hover/del:scale-110 transition-transform" />
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* No Data State */}
            {filteredCategories.length === 0 && !loading && (
                <div className="bg-white rounded-[32px] border border-gray-100 shadow-sm p-20 flex flex-col items-center justify-center text-center animate-in fade-in zoom-in-95 duration-500">
                    <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center mb-6">
                        <Tag size={32} className="text-gray-300" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">No Categories Found</h3>
                    <p className="text-gray-500 max-w-xs mb-8">Start by defining categories to organize your inventory catalog.</p>
                    <Button
                        onClick={() => { setSelectedCategory(null); setIsAddModalOpen(true); }}
                        className="rounded-xl h-12 bg-slate-900 hover:bg-slate-800 text-white gap-2 font-bold shadow-lg"
                    >
                        <Plus size={18} />
                        Add First Category
                    </Button>
                </div>
            )}

            <AddCategoryModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onRefresh={fetchData}
                category={selectedCategory}
            />

            <DeleteConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={handleDelete}
                title="Delete Category"
                message={`Are you sure you want to delete ${selectedCategory?.name}? This will affect any items currently assigned to this category.`}
            />
        </div>
    );
};

// --- Delete Confirm Modal ---
const DeleteConfirmModal = ({ isOpen, onClose, onConfirm, title, message }: any) => {
    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-[32px] p-0 overflow-hidden border-none shadow-2xl animate-in fade-in zoom-in-95">
                <div className="p-6 sm:p-8 space-y-4 max-h-[90vh] overflow-y-auto custom-scrollbar">
                    <div className="flex flex-col items-center text-center space-y-4">
                        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-red-50 rounded-3xl flex items-center justify-center text-red-500 mb-2">
                            <XCircle size={40} className="sm:w-12 sm:h-12" strokeWidth={1.5} />
                        </div>
                        <div>
                            <h3 className="text-lg sm:text-xl font-bold text-gray-900">{title}</h3>
                            <p className="text-[10px] sm:text-sm text-gray-500 mt-2 font-medium leading-relaxed px-2">{message}</p>
                        </div>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <Button
                            variant="outline"
                            onClick={onClose}
                            className="order-2 sm:order-1 flex-1 rounded-2xl h-12 font-bold border-gray-100 hover:bg-gray-50 text-gray-400"
                        >
                            Keep Category
                        </Button>
                        <Button
                            onClick={onConfirm}
                            className="order-1 sm:order-2 flex-1 rounded-2xl h-12 bg-red-600 hover:bg-red-700 text-white font-bold shadow-lg shadow-red-600/20"
                        >
                            Delete
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ItemCategories;
